import { useState, useEffect, useMemo, Fragment } from 'react';
import { useIsMobile } from '../hooks/use-mobile';
import { Separator } from '../components/ui/separator';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "../components/ui/breadcrumb";
import { SidebarTrigger } from "../components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar as CalendarIcon,
  Loader2,
  X,
  Settings,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { NavigationProps } from "../types/navigation";
import { Badge } from '../components/ui/badge';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { Transaction, RecurringTransaction } from "../types/transaction";
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Transition } from '@headlessui/react';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { mockRecurringTransactions, mockTransactions } from '../lib/mockData';

// Define Event interface locally for now
interface Event {
  id?: string; // Firestore document ID
  userId: string;
  title: string;
  date: string; // YYYY-MM-DD format
  description?: string;
  createdAt: Date | null; // Or string, depending on how you want to handle it without serverTimestamp
}

interface CalendarPageProps extends NavigationProps {}

// --- Calendar Day Data Structure ---
interface CalendarDay {
  day: number;
  month: number; // 0-indexed
  year: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  dateString: string; // YYYY-MM-DD
}

// Helper function to format date string for display
const formatDateString = (dateString: string): string => {
  const date = new Date(`${dateString}T00:00:00`); // Ensure correct date parsing
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export default function CalendarPage({ onNavigate }: CalendarPageProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  // const [transactions, setTransactions] = useState<Transaction[]>([]); // Remove Firestore transaction state
  // Add state for events if needed
  // const [events, setEvents] = useState<Event[]>([]); 
  const { currentUser } = useAuth();
  const isMobileView = useIsMobile();

  const [selectedDateString, setSelectedDateString] = useState<string | null>(null);

  // State for Add Event Dialog
  const [isAddEventDialogOpen, setIsAddEventDialogOpen] = useState(false);
  const [newEventTitle, setNewEventTitle] = useState("");
  const [newEventDate, setNewEventDate] = useState(""); 
  const [newEventDescription, setNewEventDescription] = useState("");
  const [isSavingEvent, setIsSavingEvent] = useState(false);

  // State for Badge Visibility Control - Updated
  const [badgeVisibility, setBadgeVisibility] = useState({
    transactions: true,
    recurringIncome: true,
    recurringExpenses: true,
    // Remove old ones
    // payday: true,
    // rent: true,
    // statements: true,
    // bills: true,
  });

  // Get month and year from current date
  const currentMonth = currentDate.getMonth(); // 0-indexed
  const currentYear = currentDate.getFullYear();

  // Helper functions for calendar
  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    // Returns 0 (Sun) to 6 (Sat)
    return new Date(year, month, 1).getDay(); 
  };

  // --- Calculate Recurring Events for the Grid View (Previous, Current, Next Month) --- 
  const recurringEventsForMonth = useMemo(() => {
    const eventsMap: { [key: string]: RecurringTransaction[] } = {};
    // Calculate start of the previous month and end of the next month 
    // relative to the currently viewed month to cover the whole grid.
    const viewStart = new Date(currentYear, currentMonth - 1, 1); // Start of previous month
    const viewEnd = new Date(currentYear, currentMonth + 2, 0); // End of next month

    mockRecurringTransactions.forEach(tx => {
      if (!tx.predicted_next_date) return; // Skip if no predicted date

      // TODO: Add logic here to handle different frequencies (BI_WEEKLY, etc.)
      //       and calculate all occurrences within the view range.
      // For now, just check if the predicted_next_date falls within the extended view.
      try {
          const predictedDate = new Date(`${tx.predicted_next_date}T00:00:00`);
          // Check if the predicted date is within the 3-month window
          if (predictedDate >= viewStart && predictedDate <= viewEnd) {
            const dateStr = tx.predicted_next_date;
            if (!eventsMap[dateStr]) {
              eventsMap[dateStr] = [];
            }
            eventsMap[dateStr].push(tx);
          }
      } catch(e) {
        console.error("Error parsing predicted_next_date:", tx.predicted_next_date, e);
      }
    });

    return eventsMap;
  }, [currentYear, currentMonth]);

  // --- Calculate Transactions for the Current Month --- 
  const transactionsForMonth = useMemo(() => {
    const txMap: { [key: string]: Transaction[] } = {};
    const monthStart = new Date(currentYear, currentMonth, 1);
    const monthEnd = new Date(currentYear, currentMonth + 1, 0);

    mockTransactions.forEach(tx => {
      try {
          const txDate = new Date(`${tx.date}T00:00:00`);
          if (txDate >= monthStart && txDate <= monthEnd) {
            const dateStr = tx.date;
            if (!txMap[dateStr]) {
              txMap[dateStr] = [];
            }
            txMap[dateStr].push(tx);
          }
      } catch(e) {
        console.error("Error parsing transaction date:", tx.date, e);
      }
    });

    return txMap;
  }, [currentYear, currentMonth]);

  // Memoize filtered transactions and events for the selected day
  const selectedDayTransactions = useMemo(() => {
    if (!selectedDateString) return [];
    // Find transactions for the selected date from the processed map
    return transactionsForMonth[selectedDateString] || []; 
  }, [selectedDateString, transactionsForMonth]);
  
  // const selectedDayEvents = useMemo(() => { // Placeholder for events
  //   if (!selectedDateString || !events) return [];
  //   return events.filter(ev => ev.date === selectedDateString);
  // }, [events, selectedDateString]);

  // Function to get transactions for a specific day string (YYYY-MM-DD) - Updated
  const getTransactionsForDateString = (dateStr: string): Transaction[] => {
      // Get from the processed map
      return transactionsForMonth[dateStr] || []; 
  };

  // --- Calendar Grid Generation ---
  const generateCalendarGrid = (): CalendarDay[][] => {
      const today = new Date();
      const todayDateString = today.toISOString().split('T')[0];

      const daysInCurrentMonth = getDaysInMonth(currentYear, currentMonth);
      const firstDayOfCurrentMonth = getFirstDayOfMonth(currentYear, currentMonth); // 0=Sun, 6=Sat

      // Adjust for Monday start (0=Mon, 6=Sun)
      const adjustedFirstDay = (firstDayOfCurrentMonth === 0) ? 6 : firstDayOfCurrentMonth - 1;

      const prevMonth = (currentMonth === 0) ? 11 : currentMonth - 1;
      const prevMonthYear = (currentMonth === 0) ? currentYear - 1 : currentYear;
      const daysInPrevMonth = getDaysInMonth(prevMonthYear, prevMonth);

      const nextMonth = (currentMonth === 11) ? 0 : currentMonth + 1;
      const nextMonthYear = (currentMonth === 11) ? currentYear + 1 : currentYear;

      const calendarDays: CalendarDay[] = [];

      // 1. Days from Previous Month
      for (let i = 0; i < adjustedFirstDay; i++) {
          const day = daysInPrevMonth - adjustedFirstDay + 1 + i;
          const dateString = `${prevMonthYear}-${String(prevMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          calendarDays.push({
              day, month: prevMonth, year: prevMonthYear,
              isCurrentMonth: false, isToday: dateString === todayDateString, dateString,
          });
      }

      // 2. Days from Current Month
      for (let day = 1; day <= daysInCurrentMonth; day++) {
          const dateString = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          calendarDays.push({
              day, month: currentMonth, year: currentYear,
              isCurrentMonth: true, isToday: dateString === todayDateString, dateString,
          });
      }

      // Determine total cells needed (aim for 5 or 6 rows)
      const totalCellsNeeded = (adjustedFirstDay + daysInCurrentMonth <= 35) ? 35 : 42; 

      // 3. Days from Next Month
      const remainingCells = totalCellsNeeded - calendarDays.length;
      for (let i = 1; i <= remainingCells; i++) {
          const day = i;
          const dateString = `${nextMonthYear}-${String(nextMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          calendarDays.push({
              day, month: nextMonth, year: nextMonthYear,
              isCurrentMonth: false, isToday: dateString === todayDateString, dateString,
          });
      }

      // Chunk into weeks (rows)
      const grid: CalendarDay[][] = [];
      for (let i = 0; i < calendarDays.length; i += 7) {
          grid.push(calendarDays.slice(i, i + 7));
      }

      return grid;
  };

  const calendarGrid = generateCalendarGrid();

  // Navigation functions (remain the same)
  const goToPreviousMonth = () => { setCurrentDate(new Date(currentYear, currentMonth - 1, 1)); };
  const goToNextMonth = () => { setCurrentDate(new Date(currentYear, currentMonth + 1, 1)); };
  const formatMonth = (date: Date) => { return date.toLocaleString('default', { month: 'long', year: 'numeric' }); };


  // Days of the week
  const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const shortDaysOfWeek = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  // --- Add Event Logic --- (remains mostly the same, but remove Firestore)
  const handleAddEvent = async () => { 
    if (!currentUser || !newEventTitle || !newEventDate) { 
      console.error("Missing user, title, or date for new event.");
      return;
    }
    setIsSavingEvent(true);
    try {
      console.log("Mock Event Added (not saved to DB):", {
        userId: currentUser?.uid,
        title: newEventTitle,
        date: newEventDate,
        description: newEventDescription,
        createdAt: new Date(), // Use local date for mock
      });
      console.log("Event added successfully (locally)! Needs DB implementation.");
      setNewEventTitle("");
      setNewEventDate("");
      setNewEventDescription("");
      setIsAddEventDialogOpen(false);
      // TODO: Refetch events or update local state
    } catch (error) {
      console.error("Error adding event: ", error);
    } finally {
      setIsSavingEvent(false);
    }
  };

  // Handle clicking on a day cell - Updated
  const handleDayClick = (dayObj: CalendarDay | null) => {
    if (!dayObj || !dayObj.isCurrentMonth) { // Only allow selecting days in the current month
      // Optionally clear selection or do nothing
      // setSelectedDateString(null); 
      return; 
    }
    
    // Toggle selection for the clicked date string
    setSelectedDateString(prevSelected => prevSelected === dayObj.dateString ? null : dayObj.dateString);
  };

  return (
    <>
      <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2 px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>Calendar</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </header>
      
      <main className="flex-1 overflow-auto p-6">
        <div className="flex justify-between items-center mb-6">
           <div>
              <h1 className="text-2xl font-bold tracking-tight">Calendar</h1>
              <p className="text-muted-foreground">View and manage your schedule</p>
            </div>
            {/* Add Event Dialog Trigger */}
            <Dialog open={isAddEventDialogOpen} onOpenChange={setIsAddEventDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="default" className="gap-1">
                  <Plus className="h-4 w-4" />
                  New Event
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Add New Event</DialogTitle>
                  <DialogDescription>
                    Enter the details for your new calendar event. 
                    {selectedDateString && ` Event will be added to ${formatDateString(selectedDateString)}.`}
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="event-title" className="text-right">Title</Label>
                    <Input id="event-title" value={newEventTitle} onChange={(e) => setNewEventTitle(e.target.value)} className="col-span-3" placeholder="e.g., Team Meeting" />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="event-date" className="text-right">Date</Label>
                    <Input id="event-date" type="date" value={newEventDate || selectedDateString || ''} onChange={(e) => setNewEventDate(e.target.value)} className="col-span-3" />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="event-description" className="text-right">Description</Label>
                    <Textarea id="event-description" value={newEventDescription} onChange={(e) => setNewEventDescription(e.target.value)} className="col-span-3" placeholder="Optional details..." rows={3} />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsAddEventDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" onClick={handleAddEvent} disabled={isSavingEvent}>
                    {isSavingEvent ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</>) : ('Save Event')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
        </div>
        
        {/* Main Content Area: Calendar + Details */}
        <div className="flex flex-col lg:flex-row gap-6">
          
          {/* Calendar Card */}
          <Card className="flex-1 relative">
            <CardHeader> 
              <div className="flex flex-row items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={goToPreviousMonth} className="h-8 w-8 p-0">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <CardTitle className="text-lg">{formatMonth(currentDate)}</CardTitle>
                  <Button variant="outline" size="sm" onClick={goToNextMonth} className="h-8 w-8 p-0">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                {/* Badge Visibility Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0">
                      <Settings className="h-4 w-4" />
                      <span className="sr-only">Badge Settings</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Show Badges</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuCheckboxItem
                      checked={badgeVisibility.transactions}
                      onCheckedChange={(checked) => setBadgeVisibility(prev => ({ ...prev, transactions: !!checked }))}
                      onSelect={(e) => e.preventDefault()}
                    >
                      Transactions
                    </DropdownMenuCheckboxItem>
                    <DropdownMenuCheckboxItem
                      checked={badgeVisibility.recurringIncome}
                      onCheckedChange={(checked) => setBadgeVisibility(prev => ({ ...prev, recurringIncome: !!checked }))}
                      onSelect={(e) => e.preventDefault()}
                    >
                      Recurring Income
                    </DropdownMenuCheckboxItem>
                    <DropdownMenuCheckboxItem
                      checked={badgeVisibility.recurringExpenses}
                      onCheckedChange={(checked) => setBadgeVisibility(prev => ({ ...prev, recurringExpenses: !!checked }))}
                      onSelect={(e) => e.preventDefault()}
                    >
                      Recurring Expenses
                    </DropdownMenuCheckboxItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
            <CardContent>
              <TooltipProvider>
                <div className="overflow-hidden rounded-md border">
                  <Table className="table-fixed w-full border-collapse">
                    <TableHeader>
                       <TableRow className="border-b">
                        {daysOfWeek.map((day, index) => (
                          <TableHead 
                            key={day} 
                            className="text-center p-1 sm:p-2 h-8 w-[14.28%] border-r last:border-r-0 bg-muted/50"
                          >
                            <span className="hidden sm:inline">{day}</span>
                            <span className="sm:hidden">{shortDaysOfWeek[index]}</span>
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {calendarGrid.map((week, weekIndex) => (
                        <TableRow key={`week-${weekIndex}`} className="border-b last:border-b-0">
                          {week.map((dayObj, dayIndex) => {
                            // Destructure day object
                            const { day, isCurrentMonth, isToday, dateString } = dayObj;
                            
                            // Check if this specific date is selected
                            const isSelected = selectedDateString === dateString;
                            
                            // Get transactions ONLY if it's a current month day
                            const dayTransactions = isCurrentMonth ? getTransactionsForDateString(dateString) : [];

                            // Get recurring events for this day
                            const dayRecurringEvents = recurringEventsForMonth[dateString] || [];

                            // Calculate total activity only for current month days
                            const totalActivity = isCurrentMonth ? dayTransactions
                              .reduce((sum, tx) => sum + Math.abs(tx.amount), 0) : 0;

                            // Format the total activity amount
                            const formattedActivity = totalActivity.toLocaleString('en-US', {
                              style: 'currency', currency: 'USD', // Use dynamic currency later
                              minimumFractionDigits: 0, maximumFractionDigits: 0,
                            });

                            return (
                              <TableCell 
                                key={dateString} // Use unique dateString as key
                                onClick={() => handleDayClick(dayObj)} 
                                className={`h-16 md:h-24 p-0 w-[14.28%] align-top relative border-r last:border-r-0 transition-colors ${ // Style based on state
                                  isSelected && isCurrentMonth ? 'bg-primary/10' // Selected in current month (removed ring styling)
                                  : isToday && isCurrentMonth ? 'bg-muted/60' // Today in current month
                                  : isCurrentMonth ? 'hover:bg-muted/30 cursor-pointer' // Default current month day
                                  : 'bg-muted/10 text-muted-foreground cursor-default' // Out-of-month day
                                }`}
                              >
                                <div className="p-1 sm:p-2 overflow-hidden h-full flex flex-col">
                                   {/* Tooltip Trigger Area */}
                                   {(isCurrentMonth && dayTransactions.length > 0) ? (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          {/* Cell Content with Tooltip */}
                                          <div className="flex flex-col items-center">
                                            {/* Day Number */}
                                            <div className={`text-xs sm:text-sm w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full mb-1 ${ 
                                                isToday && !isSelected ? 'bg-primary text-primary-foreground' : // Today (not selected)
                                                isSelected ? 'bg-primary text-primary-foreground' : // Selected (takes precedence)
                                                '' // Default
                                            }`}>
                                              {day}
                                            </div>
                                            {/* Badges Area */}
                                            <div className="mt-1 flex justify-center items-center gap-1 flex-wrap"> 
                                                {/* Transaction Badge (Show only for current month days) */}
                                                {isCurrentMonth && badgeVisibility.transactions && (
                                                    <Badge variant={isSelected ? "default" : "secondary"} className="text-xs px-1.5 py-0.5"> 
                                                        {formattedActivity}
                                                    </Badge>
                                                )}
                                                {/* Recurring Event Badges (Show for all days in grid) */} 
                                                {dayRecurringEvents.map(event => (
                                                  <Fragment key={event.stream_id}>
                                                    {/* Income Badge */} 
                                                    {badgeVisibility.recurringIncome && event.last_amount?.amount && event.last_amount.amount > 0 && (
                                                      <Tooltip>
                                                        <TooltipTrigger asChild>
                                                          <Badge variant="success" className="text-xs px-1.5 py-0.5 cursor-default">
                                                            💰 {event.merchant_name || event.description?.split(' ')[0] || 'Income'}
                                                          </Badge>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                          {event.merchant_name || event.description}
                                                        </TooltipContent>
                                                      </Tooltip>
                                                    )}
                                                    {/* Expense Badge */} 
                                                    {badgeVisibility.recurringExpenses && event.last_amount?.amount && event.last_amount.amount < 0 && (
                                                      <Tooltip>
                                                        <TooltipTrigger asChild>
                                                          <Badge 
                                                            variant={event.personal_finance_category?.primary === 'RENT_AND_UTILITIES' && event.personal_finance_category?.detailed === 'RENT' ? "destructive" : "secondary"} 
                                                            className="text-xs px-1.5 py-0.5 cursor-default"
                                                          >
                                                            {event.personal_finance_category?.primary === 'RENT_AND_UTILITIES' && event.personal_finance_category?.detailed === 'RENT' ? '🏠' : '🔄'} {event.merchant_name || event.description?.split(' ')[0] || 'Expense'}
                                                          </Badge>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                          {event.merchant_name || event.description}
                                                        </TooltipContent>
                                                      </Tooltip>
                                                    )}
                                                  </Fragment>
                                                ))}
                                            </div>
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                           {/* Tooltip Content */}
                                           <div className="text-sm p-1 max-w-xs">
                                               <div className="font-semibold mb-1">
                                                {new Date(dayObj.year, dayObj.month, day).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                                              </div>
                                              {dayTransactions.length > 0 && (
                                                <div className="flex items-center justify-between mb-1">
                                                  <span className="text-muted-foreground text-xs">Total Activity:</span>
                                                  <span className="font-semibold text-xs">{formattedActivity}</span>
                                                </div>
                                              )}
                                              {dayTransactions.length > 0 && (
                                                <>
                                                  <Separator className="my-1" />
                                                  <div className="font-semibold mb-1 text-xs">Transactions:</div>
                                                  <div className="grid gap-1.5 max-h-40 overflow-y-auto">
                                                    {dayTransactions.map((tx) => (
                                                      <div key={tx.id} className="flex items-center justify-between text-xs gap-1.5">
                                                        <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                                          {tx.logo_url ? (
                                                            <img src={tx.logo_url} alt={`${tx.merchant_name || tx.name || 'Transaction'} logo`} className="h-4 w-4 shrink-0 rounded-sm object-contain" onError={(e) => (e.currentTarget.style.display = 'none')}/>
                                                          ) : (
                                                            <div className="h-4 w-4 shrink-0 rounded-sm bg-muted flex items-center justify-center text-[10px] text-muted-foreground">{tx.merchant_name ? tx.merchant_name.charAt(0).toUpperCase() : tx.name ? tx.name.charAt(0).toUpperCase() : '?'}</div>
                                                          )}
                                                          <span className="text-muted-foreground truncate">{tx.merchant_name || tx.name || 'N/A'}</span>
                                                        </div>
                                                        <span className={`font-semibold whitespace-nowrap ${tx.amount < 0 ? '' : 'text-green-600'}`}>{new Intl.NumberFormat('en-US', { style: 'currency', currency: tx.iso_currency_code || 'USD' }).format(tx.amount)}</span>
                                                      </div>
                                                    ))}
                                                  </div>
                                                </>
                                              )}
                                           </div>
                                        </TooltipContent>
                                      </Tooltip>
                                    ) : (
                                      // Cell Content (No transactions or not current month)
                                      <div className="flex flex-col items-center">
                                         {/* Day Number */}
                                         <div className={`text-xs sm:text-sm w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full mb-1 ${ 
                                              isToday && isCurrentMonth && !isSelected ? 'bg-primary text-primary-foreground' : // Today (not selected)
                                              isSelected && isCurrentMonth ? 'bg-primary text-primary-foreground' : // Selected (current month only)
                                              '' // Default
                                          }`}>
                                           {day}
                                         </div>
                                         {/* Badges Area (No Transactions) */}
                                         <div className="mt-1 flex justify-center items-center gap-1 flex-wrap">
                                             {/* Recurring Event Badges (Show for all days in grid) */} 
                                             {dayRecurringEvents.map(event => (
                                                <Fragment key={event.stream_id}>
                                                  {/* Income Badge */} 
                                                  {badgeVisibility.recurringIncome && event.last_amount?.amount && event.last_amount.amount > 0 && (
                                                    <Tooltip>
                                                      <TooltipTrigger asChild>
                                                        <Badge variant="success" className="text-xs px-1.5 py-0.5 cursor-default">
                                                          💰 {event.merchant_name || event.description?.split(' ')[0] || 'Income'}
                                                        </Badge>
                                                      </TooltipTrigger>
                                                      <TooltipContent>
                                                        {event.merchant_name || event.description}
                                                      </TooltipContent>
                                                    </Tooltip>
                                                  )}
                                                  {/* Expense Badge */} 
                                                  {badgeVisibility.recurringExpenses && event.last_amount?.amount && event.last_amount.amount < 0 && (
                                                    <Tooltip>
                                                      <TooltipTrigger asChild>
                                                        <Badge 
                                                          variant={event.personal_finance_category?.primary === 'RENT_AND_UTILITIES' && event.personal_finance_category?.detailed === 'RENT' ? "destructive" : "secondary"} 
                                                          className="text-xs px-1.5 py-0.5 cursor-default"
                                                        >
                                                          {event.personal_finance_category?.primary === 'RENT_AND_UTILITIES' && event.personal_finance_category?.detailed === 'RENT' ? '🏠' : '🔄'} {event.merchant_name || event.description?.split(' ')[0] || 'Expense'}
                                                        </Badge>
                                                      </TooltipTrigger>
                                                      <TooltipContent>
                                                        {event.merchant_name || event.description}
                                                      </TooltipContent>
                                                    </Tooltip>
                                                  )}
                                                </Fragment>
                                              ))}
                                          </div>
                                      </div>
                                    )}
                                </div>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TooltipProvider>
            </CardContent>
          </Card>

          {/* Details Card - Always Visible (Transition Removed) */}
          {/* <Transition
            show={!!selectedDateString} // Control visibility
            enter="transition ease-in-out duration-300 transform"
            enterFrom="translate-x-full opacity-0" 
            enterTo="translate-x-0 opacity-100"   
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0 opacity-100" 
            leaveTo="translate-x-full opacity-0"   
          > */}
            {/* <Wrap the Card in a div> */}
            {/* <div> */} 
              <Card className="w-full lg:w-80 xl:w-96 shrink-0"> {/* Details Card */}
                {/* Header - Updated */}
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                   <CardTitle className="text-base font-medium">
                     {/* Show selected date or a generic title */}
                     {selectedDateString ? formatDateString(selectedDateString) : 'Details'} 
                   </CardTitle>
                   {/* Close button removed */}
                   {/* <Button variant="ghost" size="sm" className="p-1 h-auto" onClick={() => setSelectedDateString(null)}>
                     <X className="h-4 w-4" />
                     <span className="sr-only">Close details</span>
                   </Button> */}
                </CardHeader>
                
                {/* Content - Updated */}
                <CardContent>
                    {selectedDateString ? ( // Check if a date is selected
                      <>
                        {/* Display Transactions */}
                        {selectedDayTransactions.length > 0 ? (
                          <div className="space-y-3">
                            <h3 className="text-sm font-semibold text-muted-foreground mb-2">Transactions</h3>
                            <div className="grid gap-2 max-h-96 overflow-y-auto pr-1"> {/* Scrollable area */}
                              {selectedDayTransactions.map((tx) => (
                                  <div key={tx.id} className="flex items-center justify-between text-sm gap-2 p-2 rounded-md hover:bg-muted/50">
                                      <div className="flex items-center gap-2 flex-1 min-w-0">
                                          {tx.logo_url ? (
                                          <img src={tx.logo_url} alt={`${tx.merchant_name || tx.name || 'Transaction'} logo`} className="h-5 w-5 shrink-0 rounded-sm object-contain" onError={(e) => (e.currentTarget.style.display = 'none')}/>
                                          ) : (
                                          <div className="h-5 w-5 shrink-0 rounded-sm bg-muted flex items-center justify-center text-xs text-muted-foreground">{tx.merchant_name ? tx.merchant_name.charAt(0).toUpperCase() : tx.name ? tx.name.charAt(0).toUpperCase() : '?'}</div>
                                          )}
                                          <span className="text-foreground truncate">{tx.merchant_name || tx.name || 'N/A'}</span>
                                      </div>
                                      <span className={`font-medium whitespace-nowrap ${tx.amount < 0 ? '' : 'text-green-600'}`}>{new Intl.NumberFormat('en-US', { style: 'currency', currency: tx.iso_currency_code || 'USD' }).format(tx.amount)}</span>
                                  </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">No transactions for this day.</p>
                        )}
                        
                        {/* Placeholder for Events */}
                        <Separator className="my-4" />
                        <h3 className="text-sm font-semibold text-muted-foreground mb-2">Events</h3>
                        <p className="text-sm text-muted-foreground">No events scheduled for this day.</p>
                        {/* TODO: Fetch and display events for selectedDateString */}
                      </>
                    ) : (
                       // Placeholder when no date is selected
                      <p className="text-sm text-muted-foreground pt-2">Select a day from the calendar to see details.</p>
                    )}
                </CardContent>

                {/* Footer - Button Removed */}
                 <CardFooter className="pt-4">
                     {/* Removed Add Event Button */}
                     {/* <Button size="sm" className="w-full gap-1" onClick={() => {
                         if (selectedDateString) { // Ensure date is selected
                             setNewEventDate(selectedDateString); 
                             setIsAddEventDialogOpen(true); 
                         }
                     }}>
                         <Plus className="h-4 w-4" /> Add Event to this Day
                     </Button> */}
                 </CardFooter>
              </Card>
            {/* </div> */} {/* End of removed div */}
          {/* </Transition> */} {/* End of removed Transition */}

        </div> 
      </main>
    </>
  );
} 